import { Button as AntButton, type ButtonProps as AntButtonProps } from "antd/es";
import type { ButtonGroupProps } from "antd/es/button/button-group";
import React from "react";

import { cv, cx } from "~/tools/class-variance";

import { useFormBoundary } from "../form/use-form-boundary";

import styles from "./button.module.css";

const variants = cv(styles.button, {
	variants: {
		type: {
			default: styles.default,
			primary: styles.primary,
			ghost: null,
			dashed: null,
			link: null,
			secondary: styles.secondary,
			dark: styles.dark,
		},

		layout: {
			inline: styles.layoutInline,
			block: styles.layoutBlock,
		},

		active: {
			true: styles.active,
		},

		compact: {
			true: styles.compact,
		},
	},
});

export declare type ButtonType = "default" | "primary" | "ghost" | "dashed" | "link" | "secondary" | "dark";
export declare type ButtonSize = "small" | "middle" | "large" | "tiny";
export declare type ButtonHtmlType = "reset" | "button" | "submit";
export declare type ButtonShape = "circle" | "circle-outline";
export type ButtonLayout = "inline" | "block";

/** Documentation is missing in the currently used version, it can be found in the old antd docs at the link https://3x.ant.design/components/button/ */
export const ButtonGroup: React.FC<React.PropsWithChildren<ButtonGroupProps>> = (props) => (
	<AntButton.Group {...props} />
);

export interface ButtonProps {
	form?: string;
	id?: string;
	children?: React.ReactNode;
	className?: string;
	disabled?: boolean;
	type?: ButtonType;
	htmlType?: ButtonHtmlType;
	href?: string;
	size?: ButtonSize;
	icon?: React.ReactElement;
	style?: React.CSSProperties;
	onClick?: React.MouseEventHandler<any>;
	onMouseEnter?: React.MouseEventHandler<any>;
	onMouseLeave?: React.MouseEventHandler<any>;
	onMouseDown?: () => void;
	onMouseUp?: () => void;
	ghost?: boolean;
	loading?: boolean;
	shape?: ButtonShape;
	layout?: ButtonLayout;
	title?: string;
	active?: boolean;
	compact?: boolean;
}

/**
 * @deprecated Use ~/components/button instead
 */
export const Button = React.forwardRef<HTMLElement, ButtonProps>(
	({ type, className, size, shape, active, compact, layout, ...props }, ref) => {
		const formId = useFormBoundary();

		return (
			<AntButton
				className={variants({
					type,
					layout,
					active,
					compact,
					className: cx(
						{
							"ant-btn-tiny": size === "tiny",
							"ant-btn-link": type === "link",
							"ant-btn-secondary": type === "secondary",
						},
						className,
					),
				})}
				form={formId}
				ref={ref}
				shape={shape as AntButtonProps["shape"]}
				size={size !== "tiny" ? size : undefined}
				type={type as AntButtonProps["type"]}
				{...props}
			/>
		);
	},
);
